import React, { useEffect, useState, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import './Services.css';

const API_BASE = process.env.REACT_APP_FILE_API_URL;

const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const Files = () => {
  const { user } = useAuth0();
  const username = user?.nickname || user?.name || user?.email;
  const [files, setFiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInput = useRef();

  const fetchFiles = async () => {
    setError('');
    try {
      const res = await fetch(`${API_BASE}/files/user/${username}`, {
        headers: {
          'Authorization': 'Basic YWRtaW46YWRtaW4='
        }
      });
      if (!res.ok) throw new Error('Failed to fetch files');
      setFiles(await res.json());
    } catch (e) {
      setError(`Could not load files: ${e.message}`);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/files/user-stats/${username}`, {
        headers: {
          'Authorization': 'Basic YWRtaW46YWRtaW4='
        }
      });
      if (!res.ok) throw new Error('Failed to fetch stats');
      setStats(await res.json());
    } catch (error) {
      console.log('Stats fetch failed:', error.message);
      setStats(null);
    }
  };

  useEffect(() => {
    if (username) {
      fetchFiles();
      fetchStats();
    }
    // eslint-disable-next-line
  }, [username]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!fileInput.current.files[0]) return;
    setUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', fileInput.current.files[0]);
    try {
      const res = await fetch(`${API_BASE}/files/upload?userId=${username}`,
        { method: 'POST', body: formData });
      if (!res.ok) throw new Error('Upload failed');
      await fetchFiles();
      await fetchStats();
      fileInput.current.value = '';
    } catch {
      setError('Upload failed.');
    }
    setUploading(false);
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await fetch(`${API_BASE}/files/user/${fileId}`, { method: 'DELETE' });
      await fetchFiles();
      await fetchStats();
    } catch {
      setError('Delete failed.');
    }
  };

  const handleDownload = (fileId) => {
    window.open(`${API_BASE}/files/download/${fileId}`, '_blank');
  };

  return (
    <div className="profile-page file-manager-page">
      <h1 className="profile-title">Your Files</h1>
      {stats && (
        <div className="file-stats">
          <span>Files: <b>{stats.fileCount}</b></span>
          <span>Storage Used: <b>{stats.readableSize || formatBytes(stats.sizeInBytes)}</b></span>
        </div>
      )}
      <form className="file-upload-form" onSubmit={handleUpload}>
        <input type="file" ref={fileInput} disabled={uploading} />
        <button type="submit" disabled={uploading}>Upload</button>
      </form>
      {error && <div className="file-error">{error}</div>}
      <div className="file-list">
        {files.length === 0 && <div className="profile-details">No files uploaded yet.</div>}
        {files.map((file) => (
          <div className="file-card" key={file.fileId}>
            <div className="file-info">
              <span className="file-name">{file.fileName}</span>
              <span className="file-size">{file.size ? formatBytes(file.size) : ''}</span>
            </div>
            <div className="file-actions">
              <button onClick={() => handleDownload(file.fileId)} title="Download">⬇️</button>
              <button onClick={() => handleDelete(file.fileId)} title="Delete">🗑️</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Files;
